import React, { useState, useEffect } from 'react';
import apiClient from '../api/api';
import { FaPlus, FaTrash, FaEdit, FaLock, FaSortAmountDown, FaGripVertical, FaTimes, FaCheckCircle, FaUserTag } from 'react-icons/fa';

const FieldItem = ({ field, onDelete }) => (
    <div className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl mb-2 group hover:border-blue-300 transition-all shadow-sm">
        <div className="flex items-center gap-3">
            <FaGripVertical className="text-slate-300 cursor-move" />
            <div>
                <p className="text-sm font-bold text-slate-700">{field.label}</p>
                <p className="text-[10px] text-slate-400 font-mono uppercase">{field.type} • {field.required ? 'Required' : 'Optional'}</p>
            </div>
        </div>
        <button onClick={() => onDelete(field.id)} className="text-slate-300 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition-colors">
            <FaTrash size={12} />
        </button>
    </div>
);

const StepCard = ({ step, onAddField, onDeleteField, onDeleteStep }) => (
    <div className="bg-slate-50 p-6 rounded-[2.5rem] border border-slate-200 relative group hover:border-emerald-200 transition-all flex flex-col h-full shadow-sm">
        <div className="flex justify-between items-start mb-6">
            <div>
                <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-black text-slate-800">{step.title}</h3>
                    {step.is_locked && <FaLock className="text-orange-400 text-xs" title="Locked Step" />}
                </div>
                <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${
                        step.role_type === 'worker' ? 'bg-purple-100 text-purple-700' :
                        step.role_type === 'buyer' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-200 text-slate-600'
                    }`}>
                        {step.role_type}
                    </span>
                </div>
                <p className="text-xs text-slate-500 font-medium">{step.description}</p>
            </div>
            {!step.is_locked && (
                <button onClick={() => onDeleteStep(step.id)} className="text-slate-300 hover:text-red-500 p-2 hover:bg-red-50 rounded-full transition-all">
                    <FaTrash size={14} />
                </button>
            )}
        </div>

        <div className="space-y-2 flex-grow overflow-y-auto max-h-64 custom-scrollbar pr-1">
            {step.fields && step.fields.map(field => (
                <FieldItem key={field.id} field={field} onDelete={onDeleteField} />
            ))}
            {(!step.fields || step.fields.length === 0) && <p className="text-xs text-slate-400 italic text-center py-8 bg-white/50 rounded-2xl border border-dashed border-slate-200">No fields yet.</p>}
        </div>

        <button 
            onClick={() => onAddField(step.id)}
            className="w-full mt-6 py-3 bg-white border-2 border-dashed border-slate-200 text-slate-400 rounded-xl text-xs font-black uppercase tracking-widest hover:border-emerald-400 hover:text-emerald-600 transition-all flex items-center justify-center gap-2 group-hover:bg-emerald-50/30"
        >
            <FaPlus /> Add Field
        </button>
    </div>
);

const AdminFormBuilderPage = () => {
    const [schema, setSchema] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Field Modal State
    const [showFieldModal, setShowFieldModal] = useState(false);
    const [activeStepId, setActiveStepId] = useState(null);
    const [newField, setNewField] = useState({ label: '', name: '', type: 'text', required: false, options: [] });

    // Step Modal State
    const [showStepModal, setShowStepModal] = useState(false);
    const [newStep, setNewStep] = useState({ title: '', description: '', role_type: 'all' });

    const fetchSchema = async () => {
        try {
            setLoading(true);
            const res = await apiClient.get('/forms/schema');
            setSchema(res.data);
        } catch (err) {
            alert("Failed to load form schema.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSchema();
    }, []);

    const handleAddField = async (e) => {
        e.preventDefault();
        try {
            const fieldConfig = {};
            if (newField.type === 'file') {
                fieldConfig.maxSize = newField.maxSize || 5;
                fieldConfig.allowedTypes = newField.allowedTypes && newField.allowedTypes.length > 0 
                    ? newField.allowedTypes 
                    : ['jpg', 'png', 'jpeg', 'pdf', 'doc', 'docx'];
            }
            if (newField.type === 'select') {
                fieldConfig.options = newField.options || [];
            }

            const fieldData = {
                ...newField,
                step_id: activeStepId,
                name: newField.name || newField.label.toLowerCase().replace(/\s+/g, '_'),
                config: fieldConfig
            };
            await apiClient.post('/forms/fields', fieldData);
            setShowFieldModal(false);
            setNewField({ label: '', name: '', type: 'text', required: false, options: [] });
            fetchSchema();
        } catch (err) {
            alert("Failed to add field.");
        }
    };

    const handleAddStep = async (e) => {
        e.preventDefault();
        try {
            await apiClient.post('/forms/steps', { ...newStep, order: schema.length + 1 });
            setShowStepModal(false);
            setNewStep({ title: '', description: '', role_type: 'all' });
            fetchSchema();
        } catch (err) {
            alert("Failed to add step.");
        }
    };

    const handleDeleteField = async (id) => {
        if(!window.confirm("Delete this field?")) return;
        try {
            await apiClient.delete(`/forms/fields/${id}`);
            fetchSchema();
        } catch (err) {
            alert("Failed to delete field.");
        }
    };

    const handleDeleteStep = async (id) => {
        if(!window.confirm("Delete this entire step and all its fields?")) return;
        try {
            await apiClient.delete(`/forms/steps/${id}`);
            fetchSchema();
        } catch (err) {
            alert(err.response?.data?.message || "Failed to delete step.");
        }
    };

    const handleOptionAdd = () => {
        const opt = prompt("Enter option value:");
        if(opt) {
            setNewField(prev => ({ ...prev, options: [...(prev.options || []), opt] }));
        }
    };

    if (loading) return <div className="p-10 text-center font-bold text-slate-500">Initializing Builder...</div>;

    return (
        <div className="space-y-8 font-sans pb-20">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight">Form Configuration</h1>
                    <p className="text-slate-500 mt-1 font-medium text-sm">Design the dynamic onboarding flow for both Workers and Buyers.</p>
                </div>
                <button 
                    onClick={() => setShowStepModal(true)}
                    className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-sm shadow-xl hover:bg-slate-800 transition-all flex items-center gap-3 transform active:scale-95 border-b-4 border-slate-700"
                >
                    <FaPlus /> Add New Step
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 items-stretch">
                {schema.map(step => (
                    <StepCard 
                        key={step.id} 
                        step={step} 
                        onAddField={(id) => { setActiveStepId(id); setShowFieldModal(true); }}
                        onDeleteField={handleDeleteField}
                        onDeleteStep={handleDeleteStep}
                    />
                ))}
            </div>

            {/* --- Add Step Modal --- */}
            {showStepModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col relative">
                        <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-white">
                            <h2 className="text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
                                <FaPlus className="text-emerald-500" /> New Flow Step
                            </h2>
                            <button onClick={() => setShowStepModal(false)} className="text-slate-400 hover:text-red-500 transition-colors p-2 hover:bg-slate-50 rounded-full">
                                <FaTimes size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleAddStep} className="p-8 space-y-6">
                            <div className="space-y-2">
                                <label className="text-xs font-black text-slate-400 uppercase ml-1">Step Title</label>
                                <input 
                                    required 
                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-700 focus:ring-2 ring-emerald-500 outline-none transition-all"
                                    value={newStep.title}
                                    onChange={e => setNewStep({...newStep, title: e.target.value})}
                                    placeholder="e.g. Identity Verification"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-black text-slate-400 uppercase ml-1">Target User Role</label>
                                <div className="grid grid-cols-3 gap-3">
                                    {['all', 'worker', 'buyer'].map(role => (
                                        <button 
                                            key={role}
                                            type="button"
                                            onClick={() => setNewStep({...newStep, role_type: role})}
                                            className={`py-3 rounded-xl border-2 font-black text-[10px] uppercase transition-all ${
                                                newStep.role_type === role 
                                                ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-md' 
                                                : 'border-slate-100 bg-slate-50 text-slate-400 hover:bg-white'
                                            }`}
                                        >
                                            {role}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-black text-slate-400 uppercase ml-1">Short Description</label>
                                <textarea 
                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-slate-600 focus:ring-2 ring-emerald-500 outline-none transition-all"
                                    value={newStep.description}
                                    onChange={e => setNewStep({...newStep, description: e.target.value})}
                                    placeholder="What should users do in this step?"
                                    rows="3"
                                />
                            </div>
                            <div className="flex gap-4 pt-4">
                                <button type="button" onClick={() => setShowStepModal(false)} className="flex-1 py-4 bg-slate-100 text-slate-500 rounded-2xl font-black transition-all hover:bg-slate-200">Cancel</button>
                                <button type="submit" className="flex-1 py-4 bg-emerald-600 text-white rounded-2xl font-black shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all flex items-center justify-center gap-2">
                                    <FaCheckCircle /> Create Step
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- Add Field Modal --- */}
            {showFieldModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col relative max-h-[90vh]">
                        <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-white">
                            <h2 className="text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
                                <FaPlus className="text-blue-500" /> Add Data Field
                            </h2>
                            <button onClick={() => setShowFieldModal(false)} className="text-slate-400 hover:text-red-500 transition-colors p-2 hover:bg-slate-50 rounded-full">
                                <FaTimes size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleAddField} className="p-8 space-y-6 overflow-y-auto custom-scrollbar">
                            <div className="space-y-2">
                                <label className="text-xs font-black text-slate-400 uppercase ml-1">Field Label</label>
                                <input 
                                    required 
                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-700 outline-none focus:ring-2 ring-blue-500 transition-all"
                                    value={newField.label}
                                    onChange={e => setNewField({...newField, label: e.target.value})}
                                    placeholder="e.g. Bank Account Number"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-black text-slate-400 uppercase ml-1">Input Type</label>
                                <select 
                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-slate-700 outline-none appearance-none cursor-pointer"
                                    value={newField.type}
                                    onChange={e => setNewField({...newField, type: e.target.value})}
                                >
                                    <option value="text">Single Line Text</option>
                                    <option value="number">Number Input</option>
                                    <option value="textarea">Multi-line Text</option>
                                    <option value="select">Dropdown Menu</option>
                                    <option value="file">File Attachment</option>
                                    <option value="date">Calendar Date</option>
                                </select>
                            </div>

                            {/* Dropdown Options */}
                            {newField.type === 'select' && (
                                <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-200 space-y-4 shadow-inner">
                                    <div className="flex justify-between items-center">
                                        <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Dropdown List</p>
                                        <button type="button" onClick={handleOptionAdd} className="bg-white px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-black text-emerald-600 shadow-sm hover:shadow-md transition-all">+ Add Value</button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {newField.options?.map((opt, idx) => (
                                            <span key={idx} className="bg-white border border-slate-200 px-3 py-2 rounded-xl text-xs font-bold text-slate-700 flex items-center gap-2 shadow-sm animate-fade-in">
                                                {opt} 
                                                <FaTimes className="cursor-pointer text-red-400 hover:text-red-600" onClick={() => setNewField(prev => ({...prev, options: prev.options.filter((_, i) => i !== idx)}))} />
                                            </span>
                                        ))}
                                        {(!newField.options || newField.options.length === 0) && <p className="text-[10px] text-slate-400 font-bold italic py-2">No options added yet.</p>}
                                    </div>
                                </div>
                            )}

                            {/* File Upload Config */}
                            {newField.type === 'file' && (
                                <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-200 space-y-4 shadow-inner">
                                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Attachment Settings</p>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-[10px] font-bold text-slate-500 mb-1 block">Max Size (MB)</label>
                                            <input type="number" className="w-full p-3 bg-white border border-slate-200 rounded-xl font-bold" value={newField.maxSize || 5} onChange={e => setNewField({...newField, maxSize: e.target.value})} />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="text-[10px] font-bold text-slate-500 mb-2 block uppercase">Accepted Extensions</label>
                                            <div className="flex flex-wrap gap-2">
                                                {['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg'].map(ext => (
                                                    <label key={ext} className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-slate-200 cursor-pointer hover:border-blue-400 transition-all group">
                                                        <input type="checkbox" className="accent-blue-600 w-4 h-4" checked={newField.allowedTypes?.includes(ext)} onChange={e => {
                                                            const types = newField.allowedTypes || [];
                                                            setNewField({...newField, allowedTypes: e.target.checked ? [...types, ext] : types.filter(t => t !== ext)});
                                                        }} />
                                                        <span className="text-[10px] font-black text-slate-600 uppercase">.{ext}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl">
                                <input type="checkbox" id="req" className="w-6 h-6 accent-blue-600" checked={newField.required} onChange={e => setNewField({...newField, required: e.target.checked})} />
                                <label htmlFor="req" className="text-sm font-black text-slate-700 cursor-pointer">Mark as Required</label>
                            </div>
                            
                            <div className="flex gap-4 pt-4">
                                <button type="button" onClick={() => setShowFieldModal(false)} className="flex-1 py-4 bg-slate-100 text-slate-500 rounded-2xl font-black transition-all hover:bg-slate-200">Cancel</button>
                                <button type="submit" className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-black shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center justify-center gap-2">
                                    <FaCheckCircle /> Add Field
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminFormBuilderPage;